import { HttpClient } from '@angular/common/http';
import { Log } from '@shared/debug/log';
import { lastValueFrom } from 'rxjs';
import { Box3, Matrix4, Matrix4Tuple, OrthographicCamera } from 'three';

declare let _3Dconnexion: any;

export class ThreeDMouseControl {

  private _connected = false;

  private _3Dconnexion: any;

  constructor(private httpClient: HttpClient,
              private camera: OrthographicCamera,
              private renderScene: () => void) {
  }

  public async connect(): Promise<void> {
    const isAvailable = await this.checkProxyIfAvailable();
    if (isAvailable) {
      this.connectMouse();
    }
  }

  private async checkProxyIfAvailable(): Promise<boolean> {
    const host = '127.51.68.120';
    const defport = 8181;
    const url = `https://${host}:${defport}/3dconnexion/nlproxy`;

    try {
      await lastValueFrom(this.httpClient.get(url));
    } catch (_error) {
      return false;
    }
    return true;
  }

  private connectMouse(): void {
    this._3Dconnexion = new _3Dconnexion(this);
    if (!this._3Dconnexion.connect()) {
      Log.error('Cannot connect to 3Dconnexion NL-Proxy');
    }
  }

  /**
   * The following methods are called by the _3Dconnexion script
   */
  protected getUnitsToMeters() {
    return 10.0;
  }

  protected onConnect(): void {
    Log.info('3Dconnexion NL-Proxy connected');

    // Use the window so the 3d mouse is active when any part of the application has focus
    this._3Dconnexion.create3dmouse(window, 'SmartDesigner');
  }

  protected on3dmouseCreated() {
    Log.info('3d mouse created');
    this._connected = true;

    // If we already have focus tell the 3d mouse
    if (document.hasFocus()) {
      this._3Dconnexion.focus();
    }
  }

  protected onDisconnect(reason: any): void {
    Log.info('3Dconnexion NL-Proxy disconnected ' + reason?.toString());
    this._connected = false;
  }

  protected getViewMatrix(): Matrix4Tuple {
    return this.camera.matrixWorld.toArray();
  }

  protected setViewMatrix(data: Matrix4Tuple) {
    const cameraMatrix = new Matrix4();
    cameraMatrix.fromArray(data);
    cameraMatrix.decompose(this.camera.position, this.camera.quaternion, this.camera.scale);
    this.camera.updateMatrixWorld(true);
  }

  protected setViewExtents(data: number[]) {
    this.camera.left = data[0];
    this.camera.bottom = data[1];
    this.camera.right = data[3];
    this.camera.top = data[4];
    this.camera.updateProjectionMatrix();
  }

  protected getViewExtents(): number[] {
    return [this.camera.left, this.camera.bottom, -this.camera.far, this.camera.right, this.camera.top, -this.camera.near];
  }

  protected getViewRotatable(): boolean {
    return true;
  }

  protected getCoordinateSystem(): Matrix4Tuple {
    const numbers = this.camera.matrixWorld.clone().makeRotationY(Math.PI).toArray();
    return numbers;
  }

  protected getFrontView() {
    return this.getCoordinateSystem();
  }

  protected getPerspective(): boolean {
    return false;
  }

  protected setTransaction(transaction: number) {
    if (transaction === 0) {
      this.renderScene();
    }
  }

  protected getModelExtents() {
    return [...this._modelExtents.min.toArray(), ...this._modelExtents.max.toArray()];
  }

  public setModelExtents(box3: Box3) {
    if (!this._modelExtents.equals(box3)) {
      this._modelExtents = box3;
      if (this._connected) {
        this._3Dconnexion.update3dcontroller({
          "model": {
            "extents": this.getModelExtents()
          }
        });
      }
    }
  }

  private _modelExtents = new Box3().setFromArray([0, 0, 0, 0, 0, 0]);
}
